import { ref } from "vue";

import { favoritesChatId, type Chat } from "shared";
import { useRouter } from "vue-router";

import { useCrypto } from "@shared/crypto/useCrypto";
import { STORES, useIndexedDb } from "@shared/storage/useIndexedDb";

import { chats } from "@entities/chat/useChats";
import { peers, usePeers } from "@entities/peer/usePeers";

// «Избранное» — чат с собой. id детерминированный (favoritesChatId от своего
// signPubKey) и совпадает на всех устройствах аккаунта. Крипта/доставка идут по
// обычному пути (peer = ты сам, to === from), просто UI помечен isFavorites.
const favoritesId = ref<string | null>(null);

export function useFavorites() {
    const { exportSignPublicKey } = useCrypto();
    const { read: readChat, write: saveChat } = useIndexedDb(STORES.CHATS);
    const { getMyPeerInfo, savePeer } = usePeers();
    const router = useRouter();

    // Вычисляем id один раз (нужны разблокированные ключи). Резолвим на старте
    // приложения, чтобы isFavorites был корректен сразу (в т.ч. при reload/URL).
    async function resolveId(): Promise<string> {
        if (!favoritesId.value) {
            favoritesId.value = await favoritesChatId(
                await exportSignPublicKey()
            );
        }
        return favoritesId.value;
    }

    function isFavorites(chatId?: string | null): boolean {
        return !!chatId && chatId === favoritesId.value;
    }

    // Локально гарантируем Chat (established) + self-peer, чтобы useChatSession
    // открыл чат и был готов отправлять. Идемпотентно (fresh device / reload).
    async function ensureFavorites(): Promise<string> {
        const id = await resolveId();

        const existing = await readChat<Chat>(id);
        const chat: Chat = existing ?? {
            id,
            established: true,
            createdAt: Date.now(),
        };
        if (!existing) {
            await saveChat(chat, id);
        }
        if (!chats.value.find((c) => c.id === id)) {
            chats.value.push(chat);
        }

        // self-peer: моя же личность. peerKey === myKey → to === from (self).
        if (!peers.value[id]) {
            const me = await getMyPeerInfo();
            await savePeer(me, id);
        }
        return id;
    }

    async function openFavorites(): Promise<void> {
        const id = await ensureFavorites();
        router.push(`/app/chat/${id}`);
    }

    return {
        favoritesId,
        resolveId,
        isFavorites,
        ensureFavorites,
        openFavorites,
    };
}
